import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Trophy, MapPin, Building2, ChevronDown, Loader2, BarChart3 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import SocialShareButtons from "../components/SocialShareButtons";
import { toast } from "sonner";
import axios from "axios";
import { formatTranslation } from "../utils/ranks";

import { API } from "../config";

function CityRankingCard({ city, index, type }) {
  const isClean = type === "cleanest";
  const bgColor = isClean ? "bg-emerald-50" : "bg-red-50";
  const accentColor = isClean ? "text-emerald-600" : "text-red-500";
  const badgeColor = isClean ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700";

  return (
    <div className={`${bgColor} rounded-xl p-4 flex items-center gap-3`} data-testid={`city-rank-${index}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm ${badgeColor}`}>
        {city.rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-[#2B2D42] text-sm truncate">{city.city}</p>
        <p className="text-xs text-[#8D99AE]">{city.province}</p>
      </div>
      <div className="text-right shrink-0">
        <p className={`font-black text-lg ${accentColor}`}>{city.reports_per_10k}</p>
        <p className="text-[10px] text-[#8D99AE]">/ 10.000 hab.</p>
      </div>
    </div>
  );
}

function BarrioCard({ barrio, index }) {
  const intensity = Math.min(barrio.active_reports * 15, 100);
  return (
    <div className="bg-white rounded-xl p-4 flex items-center gap-3 border border-[#8D99AE]/10" data-testid={`barrio-rank-${index}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-sm bg-[#FF6B6B]/10 text-[#FF6B6B]">
        {barrio.rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-[#2B2D42] text-sm truncate">{barrio.barrio}</p>
        <p className="text-xs text-[#8D99AE]">{barrio.active_reports} {barrio.reports_label}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-black text-lg text-[#FF6B6B]">{barrio.active_reports}</p>
        <p className="text-[10px] text-[#8D99AE]">{barrio.reports_label}</p>
      </div>
      <div className="w-12 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#FF6B6B] rounded-full" style={{ width: `${intensity}%` }} />
      </div>
    </div>
  );
}

export default function RankingsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "cities");
  const [listType, setListType] = useState("dirtiest");
  const [cityData, setCityData] = useState(null);
  const [barrioData, setBarrioData] = useState(null);
  const [selectedCity, setSelectedCity] = useState(searchParams.get("city") || "Madrid");
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const { data } = await axios.get(`${API}/rankings/cities`, { withCredentials: true });
        setCityData(data);
        const uniqueCities = [...new Set(data.dirtiest.map(c => c.city))];
        setCities(uniqueCities);
      } catch { toast.error(t("rankingUi.loadError")); }
      finally { setLoading(false); }
    };
    fetchCities();
  }, [t]);

  useEffect(() => {
    if (tab !== "barrios") return;
    const fetchBarrios = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/rankings/barrios?city=${encodeURIComponent(selectedCity)}`, { withCredentials: true });
        setBarrioData(data);
      } catch { toast.error(t("rankingUi.loadBarrioError")); }
      finally { setLoading(false); }
    };
    fetchBarrios();
  }, [tab, selectedCity, t]);

  const getSharePayload = async (type) => {
    const { data } = await axios.get(
      type === "barrios"
        ? `${API}/rankings/barrios/share?city=${encodeURIComponent(selectedCity)}`
        : `${API}/rankings/cities/share?list_type=${type}`
    );
    return { title: data.title, text: data.share_text, url: data.share_url || data.app_url, imageUrl: data.image_url };
  };

  const currentList = cityData ? (listType === "dirtiest" ? cityData.dirtiest : cityData.cleanest) : [];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="rankings-page">
      <div className="ios-safe-header p-4 flex justify-between items-center">
        <Button variant="ghost" onClick={() => navigate("/")} className="text-[#8D99AE]" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />{t("backToMap")}
        </Button>
        <LanguageSelector />
      </div>

      <div className="max-w-lg mx-auto px-4 pb-20">
        <div className="text-center mb-5">
          <BarChart3 className="w-10 h-10 text-[#FF6B6B] mx-auto mb-2" />
          <h1 className="text-2xl font-black text-[#2B2D42]" style={{ fontFamily: "Nunito, sans-serif" }}>{t("rankingUi.pageTitle")}</h1>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setTab("cities")}
            className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-colors ${tab === "cities" ? "bg-[#FF6B6B] text-white" : "bg-white text-[#8D99AE] border border-[#8D99AE]/20"}`}
            data-testid="tab-cities"
          >
            <Building2 className="w-4 h-4 inline mr-1" /> {t("rankingUi.cities")}
          </button>
          <button
            onClick={() => setTab("barrios")}
            className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-colors ${tab === "barrios" ? "bg-[#FF6B6B] text-white" : "bg-white text-[#8D99AE] border border-[#8D99AE]/20"}`}
            data-testid="tab-barrios"
          >
            <MapPin className="w-4 h-4 inline mr-1" /> {t("rankingUi.neighborhoods")}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" /></div>
        ) : tab === "cities" ? (
          <>
            {/* Cleanest / Dirtiest toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setListType("dirtiest")}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${listType === "dirtiest" ? "bg-red-100 text-red-700" : "bg-white text-[#8D99AE]"}`}
                data-testid="toggle-dirtiest"
              >
                {t("rankingUi.dirtiest")}
              </button>
              <button
                onClick={() => setListType("cleanest")}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${listType === "cleanest" ? "bg-emerald-100 text-emerald-700" : "bg-white text-[#8D99AE]"}`}
                data-testid="toggle-cleanest"
              >
                {t("rankingUi.cleanest")}
              </button>
            </div>

            <p className="text-xs text-[#8D99AE] mb-3 text-center">
              {formatTranslation(t, "rankingUi.citySummary", { count: cityData?.total_cities || 0 })}
            </p>

            <p className="text-center text-sm font-semibold text-[#5C677D] px-4 mb-3">
              {t("shareUi.tagline")}
            </p>
            <div className="space-y-2 mb-4">
              {currentList.map((city, i) => (
                <CityRankingCard key={city.city} city={city} index={i} type={listType} />
              ))}
              {currentList.length === 0 && (
                <p className="text-center text-[#8D99AE] py-8">{t("rankingUi.noEnoughData")}</p>
              )}
            </div>

            <SocialShareButtons
              className="mt-4"
              prefix="city-rankings-share"
              label={t("rankingUi.shareRanking")}
              loadShareData={() => getSharePayload(listType)}
              onCopied={() => toast.success(t("rankingUi.copied"))}
              onError={() => toast.error(t("rankingUi.shareError"))}
            />
          </>
        ) : (
          <>
            {/* City selector */}
            <div className="mb-4">
              <label className="text-xs font-bold text-[#2B2D42] mb-1 block">{t("rankingUi.cityLabel")}</label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full p-3 bg-white border border-[#8D99AE]/20 rounded-xl text-sm text-[#2B2D42]"
                data-testid="city-selector"
              >
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
                {!cities.includes(selectedCity) && <option value={selectedCity}>{selectedCity}</option>}
              </select>
            </div>

            <p className="text-xs text-[#8D99AE] mb-3 text-center">
              {formatTranslation(t, "rankingUi.activeReportsInCity", { count: barrioData?.total_reports || 0, city: selectedCity })}
            </p>

            <p className="text-center text-sm font-semibold text-[#5C677D] px-4 mb-3">
              {t("shareUi.tagline")}
            </p>
            <div className="space-y-2">
              {barrioData?.barrios?.map((b, i) => (
                <BarrioCard
                  key={b.barrio}
                  barrio={{
                    ...b,
                    reports_label: t("rankingUi.reportsLabel"),
                  }}
                  index={i}
                />
              ))}
              {(!barrioData?.barrios || barrioData.barrios.length === 0) && (
                <p className="text-center text-[#8D99AE] py-8">{t("rankingUi.noNeighborhoodData")}</p>
              )}
            </div>

            {!!barrioData?.barrios?.length && (
              <>
                <SocialShareButtons
                  className="mt-4"
                  prefix="barrio-rankings-share"
                  label={t("rankingUi.shareRanking")}
                  loadShareData={() => getSharePayload("barrios")}
                  onCopied={() => toast.success(t("rankingUi.copied"))}
                  onError={() => toast.error(t("rankingUi.shareError"))}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
